<template>
  <modal
    id="add-to-collection-modal"
    @close="closeModal"
    :show.sync="$parent.modals.addToCollection"
    size="sm"
  >
    <!-- HEADER -->
    <div v-if="isLoggedIn" slot="header" class="d-flex align-items-center">
      <div
        class="artwork-thumbnail mr-2"
        :style="'background-image: url(' + addToCollectionPhotoUrl + ')'"
      ></div>
      <h6 class="modal-title" id="modal-title-default">Add to Collection</h6>
    </div>

    <div class="text-center mb-3 logo-header" v-if="!isLoggedIn">
      <div>
        <img class="logo" src="/img/brand/artgrab-logo.png" alt="Art Grab" />
        Please log in or create an account to add artwork to collections.
      </div>
      <button
        type="button"
        @click="closeModal"
        data-dismiss="modal"
        aria-label="Close"
        class="close"
      >
        <span>×</span>
      </button>
    </div>
    <div v-if="!isLoggedIn" class="login-toggle text-center">
      <base-radio
        @input="handleChangeLogin"
        name="login"
        v-model="radio.login"
        class="font-weight-bold"
        >LOG IN</base-radio
      >
      <base-radio
        @input="handleChangeLogin"
        name="create-account"
        v-model="radio.login"
        class="font-weight-bold"
        >CREATE ACCOUNT</base-radio
      >
    </div>

    <!-- LOGIN -->
    <div v-if="!isLoggedIn && radio.login === 'login'" class="login-wrapper">
      <form role="form" @submit.prevent="handleLoginSubmit">
        <base-input
          label="Email"
          class="mb-3"
          type="email"
          placeholder="Enter your email address"
          v-model="loginForm.email"
          spellcheck="false"
        ></base-input>

        <base-input
          label="Password"
          class="mb-3"
          type="password"
          placeholder="Enter your password"
          v-model="loginForm.password"
          required
        ></base-input>

        <div id="login-errors" class="login-errors" role="alert">
          {{ loginError }}
        </div>

        <div class="text-center">
          <base-button
            type="primary"
            :loading="loading"
            nativeType="input"
            block
            class="mt-4 mb-2"
            >LOG IN</base-button
          >
        </div>
      </form>
    </div>

    <!-- CREATE ACCOUNT -->
    <div
      v-if="!isLoggedIn && radio.login === 'create-account'"
      class="create-account-wrapper"
    >
      <form role="form" @submit.prevent="handleCreateAccountSubmit">
        <base-input
          label="Email"
          class="mb-3"
          type="email"
          placeholder="Enter your email address"
          v-model="createAccount.email"
          spellcheck="false"
          required
        ></base-input>

        <base-input
          label="Password"
          class="mb-3"
          type="password"
          placeholder="Enter your password"
          v-model="createAccount.password"
          required
        ></base-input>

        <base-input
          label="Confirm Password"
          class="mb-3"
          type="password"
          placeholder="Re-enter your password"
          v-model="createAccount.repassword"
          required
        ></base-input>

        <div class="text-center">
          <base-checkbox class="mb-3" v-model="termsCheckbox">
            I accept the&nbsp;
            <router-link to="/terms" target="_blank"
              >Art Grab Terms of Use</router-link
            >.
          </base-checkbox>
        </div>

        <div class="create-account-errors" role="alert">
          {{ createFormError || createAccountError }}
        </div>

        <div class="text-center">
          <base-button
            type="primary"
            :loading="createAccountLoading"
            nativeType="input"
            block
            class="mt-4 mb-2"
            >CREATE ACCOUNT</base-button
          >
        </div>
      </form>
    </div>

    <!-- CREATE COLLECTION -->
    <collapse v-if="isLoggedIn">
      <collapse-item name="1">
        <h5 slot="title" class="mb-0">CREATE COLLECTION</h5>
        <div>
          <base-input
            label="Name"
            type="text"
            input-classes="form-control-sm"
            :placeholder="'Enter a collection name'"
            v-model="newCollection.name"
            required
            spellcheck="false"
            maxlength="36"
          ></base-input>
          <div class="public-toggle d-flex">
            <div>
              <label class="form-control-label">Public</label>
            </div>
            <div>
              <base-switch
                class="ml-5"
                v-model="newCollection.is_public"
              ></base-switch>
            </div>
          </div>
          <span class="helper-text"
            >If a collection is public, you can share a link to the collection
            to allow others to view it.</span
          >
        </div>
        <base-button
          @click="createCollection"
          :loading="createLoading"
          type="primary"
          size="sm"
          block
          class="mt-3"
          >CREATE</base-button
        >
      </collapse-item>
    </collapse>

    <!-- COLLECTIONS -->
    <div v-if="isLoggedIn" class="collections-wrapper">
      <div
        v-for="collection in user.collections"
        v-bind:key="'collection-button--' + collection.id"
        class="collection-button"
        @click="selected = collection.id"
      >
        <base-radio
          :disabled="added"
          :name="collection.id"
          class="collection-radio"
          v-model="selected"
          >{{ collection.name }}</base-radio
        >
      </div>
    </div>
    <div v-if="addToCollectionError" class="error-message">
      {{ addToCollectionError }}
    </div>
    <base-button
      v-if="isLoggedIn"
      :disabled="!selected || added"
      class="mt-3"
      type="primary"
      block
      @click="addToCollection"
      :loading="loading"
      >{{ buttonText }}</base-button
    >
  </modal>
</template>

<script>
import { mapState } from "vuex";
import { Collapse, CollapseItem } from "../components";
const apiBaseURL = process.env.VUE_APP_API_URL;

export default {
  name: "add-to-collection",
  components: { Collapse, CollapseItem },
  data() {
    return {
      selected: null,
      loading: false,
      createLoading: false,
      added: false,
      addToCollectionError: null,
      newCollection: {
        name: "",
        is_public: false,
      },
      loginForm: {
        email: "",
        password: "",
      },
      createAccount: {
        email: "",
        password: "",
        repassword: "",
      },
      termsCheckbox: false,
      createFormError: "",
      createAccountLoading: false,
      radio: {
        login: "login",
      },
    };
  },
  computed: {
    ...mapState([
      "user",
      "isLoggedIn",
      "addToCollectionPhotoId",
      "addToCollectionPhotoUrl",
      "addToCollectionType",
      "loginError",
      "createAccountError",
    ]),
    buttonText() {
      if (this.loading) {
        return "ADDING";
      } else if (this.added) {
        return "ADDED 👍";
      } else {
        return "ADD";
      }
    },
  },
  methods: {
    handleChangeLogin: function (plan) {
      this.radio.login = plan;
    },
    handleLoginSubmit() {
      this.loginLoading = true;
      const {
        loginForm: { email, password },
      } = this;
      this.$store.dispatch("loginRequest", { email, password }).then(() => {
        if (this.isLoggedIn) {
          this.$store.dispatch("getCollections");
          this.$store.commit("loginError", "");
          this.loginForm.email = "";
          this.loginForm.password = "";
        }
        this.loginLoading = false;
      });
    },
    handleCreateAccountSubmit() {
      this.createAccountLoading = true;
      const {
        createAccount: { email, password, repassword },
        termsCheckbox,
      } = this;

      if (email.length >= 5) {
        if (password.length >= 8) {
          if (password == repassword) {
            if (termsCheckbox) {
              this.createFormError = "";

              this.$store
                .dispatch("createAccount", { email, password })
                .then(() => {
                  if (this.isLoggedIn) {
                    this.$store.commit("createAccountError", "");
                    this.createFormError = "";
                    this.createAccount.email = "";
                    this.createAccount.password = "";
                    this.createAccount.repassword = "";
                    this.termsCheckbox = false;
                    this.createAccountLoading = false;
                  } else {
                    this.createFormError =
                      "There was an error creating your account. Please try again or contact us.";
                  }
                });
            } else {
              this.createFormError =
                "Please accept the terms by checking the checkbox.";
              this.createAccountLoading = false;
            }
          } else {
            this.createFormError =
              "Password and confirm password do not match.";
            this.createAccountLoading = false;
          }
        } else {
          this.createFormError =
            "Password length needs to be at least 8 characters.";
          this.createAccountLoading = false;
        }
      } else {
        this.createFormError = "Please enter a valid email address.";
        this.createAccountLoading = false;
      }
    },
    createCollection() {
      this.createLoading = true;
      this.addToCollectionError = "";
      this.axios
        .request({
          url: `${apiBaseURL}/api/artv1.1/user/createcollection`,
          method: "post",
          data: {
            name: this.newCollection.name,
            is_public: this.newCollection.is_public ? 1 : 0,
          },
        })
        .then((result) => {
          if (
            result.data.data &&
            result.data.data.message === "Success" &&
            !result.data.data.api_action_error
          ) {
            this.$store.dispatch("getCollections");
            this.selected = result.data.data.collection_id.toString();
          } else if (result.data.data && result.data.data.api_action_error) {
            this.addToCollectionError = result.data.data.api_action_error;
          } else {
            this.addToCollectionError =
              "There was an error creating the collection. Please log out and log back in then try again.";
          }
        })
        .catch((error) => {
          /*eslint no-console: ["error", { allow: ["warn", "error"] }] */
          console.error(error);
          this.addToCollectionError =
            "There was an error creating the collection. Please log out and log back in then try again.";
        })
        .finally(() => {
          this.createLoading = false;
          this.newCollection.name = "";
          this.newCollection.is_public = false;
          this.$root.$emit("close-create-collection");
        });
    },
    addToCollection() {
      this.loading = true;
      this.addToCollectionError = null;
      if (this.selected && this.addToCollectionPhotoId) {
        this.axios
          .request({
            url: `${apiBaseURL}/api/artv1.1/user/addtocollection`,
            method: "post",
            data: {
              collection_id: this.selected,
              photo_id: this.addToCollectionPhotoId,
              type: this.addToCollectionType,
            },
          })
          .then((result) => {
            if (
              result.data.data.message === "Success" &&
              !result.data.data.api_action_error
            ) {
              this.$store.dispatch("getCollections");

              this.added = true;
              setTimeout(() => this.closeModal(), 1000);
            } else if (result.data.data.api_action_error) {
              this.addToCollectionError = result.data.data.api_action_error;
            }
          })
          .catch((error) => {
            /*eslint no-console: ["error", { allow: ["warn", "error"] }] */
            console.error(error);
          })
          .finally(() => (this.loading = false));
      }
    },
    closeModal() {
      this.added = false;
      this.loading = false;
      this.addToCollectionError = null;
      this.createLoading = false;
      this.newCollection.name = "";
      this.newCollection.is_public = false;
      this.$store.commit("createAccountError", "");
      this.createFormError = "";
      this.createAccount.email = "";
      this.createAccount.password = "";
      this.createAccount.repassword = "";
      this.termsCheckbox = false;
      this.createAccountLoading = false;
      this.$store.commit("showAddToCollectionModal", {
        status: false,
        photo_id: null,
        sized_url: null,
        type: null,
      });
      this.$store.commit("loginError", "");
      this.loginForm.email = "";
      this.loginForm.password = "";
      this.radio.login = "login";
    },
  },
  mounted() {
    if (this.user.collections.length > 0) {
      this.selected =
        this.user.collections[this.user.collections.length - 1].id;
    }
  },
  // watch: {
  //   user(newValue, oldValue) {
  //     if (newValue.collections.length > 0) {
  //       this.selected =
  //         newValue.collections[newValue.collections.length - 1].id;
  //     }
  //   }
  // }
};
</script>

<style lang="scss">
#add-to-collection-modal {
  .error-message,
  .create-account-errors,
  .login-errors {
    color: red;
    font-size: 0.875rem;
    text-align: center;
    margin-top: 0.75rem;
  }

  .accordion {
    .card {
      margin-bottom: 18px;

      .card-header {
        padding: 10px;
      }
    }
  }

  .logo-header {
    .logo {
      margin: 40px auto 10px;
      width: 180px;
      display: block;
    }
    .close {
      position: absolute;
      top: 20px;
      right: 20px;
      padding: 1.25rem;
      margin: -1rem -1rem -1rem auto;
    }
  }

  .artwork-thumbnail {
    width: 24px;
    height: 24px;
    background-size: cover;
    background-position: center;
    border-radius: 2px;
  }

  .login-toggle {
    padding: 0.5rem;
    background-color: #f7f7f7;
    border-radius: 3px;
    display: flex;
    justify-content: space-around;
    margin-bottom: 1rem;
  }

  .helper-text {
    font-size: 0.7rem;
    line-height: 1.7;
    display: block;
  }

  .collection-button {
    background-color: #f7f7f7;
    border-radius: 3px;
    padding: 10px;
    cursor: pointer;

    &:not(:last-child) {
      margin-bottom: 1rem;
    }

    .collection-radio {
      font-weight: 600;
      margin: 0;

      .custom-control-label {
        width: 100%;
      }
    }
  }
}
</style>
